import type { ReactNode } from 'react';

import { CircleX } from 'lucide-react';

import { cn } from '@nesso/ui/lib/utils';
import { Badge } from '@nesso/ui/primitives/badge';

const DEFAULT_DELETE_ICON = <CircleX className="w-4 h-4" />;

type Props = {
    options: { value: string; label: string }[];
    selected: string[];
    onRemove: (value: string) => void;
    deleteIcon?: ReactNode;
    disabled?: boolean;
    className?: string;
    badgeClassName?: string;
    labelClassName?: string;
};

export function SelectedBadgeList({
    options,
    selected,
    onRemove,
    deleteIcon = DEFAULT_DELETE_ICON,
    disabled = false,
    className,
    badgeClassName,
    labelClassName,
}: Props) {
    if (selected.length === 0) return null;

    const optionsByValue = new Map(options.map((o) => [o.value, o]));

    return (
        <div className={cn('flex flex-wrap gap-2', className)}>
            {selected.map((value) => {
                const option = optionsByValue.get(value);
                return option ? (
                    <Badge
                        key={value}
                        asChild
                        variant="outline"
                        className={cn(
                            'flex items-center gap-1.5 rounded-full pl-2 pr-0.5 font-normal',
                            badgeClassName,
                        )}
                    >
                        <button
                            type="button"
                            tabIndex={0}
                            aria-label={`Remove ${option.label}`}
                            title={`Remove ${option.label}`}
                            disabled={disabled}
                            onClick={(e) => {
                                e.preventDefault();
                                onRemove(value);
                            }}
                        >
                            <span className={cn('truncate text-[0.8125rem]', labelClassName)}>
                                {option.label}
                            </span>
                            <span className="opacity-70">{deleteIcon}</span>
                        </button>
                    </Badge>
                ) : null;
            })}
        </div>
    );
}
